import type { SchoolCalendarEvent, SchoolYear } from "./types";
import { formatDateOnly, getDatePart, getSchoolYearDateRange, startOfLocalDay } from "./utils";

interface BuildYearPlannerMonthsArgs {
  events: SchoolCalendarEvent[];
  schoolYear: SchoolYear | null | undefined;
  language?: string;
}

// Bouwt het maandmodel van de jaarplanner (gebruikt door de Yearplanner-weergave en de Excel-export).
export const buildYearPlannerMonths = ({ events, schoolYear, language }: BuildYearPlannerMonthsArgs) => {
  const { startDate, endDate } = getSchoolYearDateRange(schoolYear);
  if (!startDate || !endDate) return [];

  const monthFormatter = new Intl.DateTimeFormat(language || "nl-NL", {
    month: "long",
    year: "numeric",
  });
  const weekdayFormatter = new Intl.DateTimeFormat(language || "nl-NL", {
    weekday: "short",
  });

  const toLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const eventTouchesDate = (event: SchoolCalendarEvent, dateKey: string) => {
    const eventStart = getDatePart(event.startDate);
    const eventEnd = getDatePart(event.endDate) || eventStart;
    if (!eventStart) return false;
    return eventStart <= dateKey && dateKey <= eventEnd;
  };

  const months: Array<any> = [];
  let cursor = startOfLocalDay(startDate);
  const lastDay = startOfLocalDay(endDate);
  const monthByKey = new Map<string, any>();

  while (cursor <= lastDay) {
    const dateKey = toLocalDateKey(cursor);
    const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    const monthLabel = monthFormatter.format(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    const dayEvents = events.filter((event) => eventTouchesDate(event, dateKey));
    const hasHoliday = dayEvents.some((event) => ["HOLIDAY", "DAY_OFF"].includes(String(event?.eventType || "").toUpperCase()));
    const isWeekend = cursor.getDay() === 0 || cursor.getDay() === 6;

    if (!monthByKey.has(monthKey)) {
      const nextMonth = { key: monthKey, label: monthLabel, days: [] };
      monthByKey.set(monthKey, nextMonth);
      months.push(nextMonth);
    }

    monthByKey.get(monthKey)?.days.push({
      key: dateKey,
      dayNumber: String(cursor.getDate()),
      weekday: weekdayFormatter.format(cursor),
      dateLabel: formatDateOnly(dateKey, language),
      isWeekend,
      hasHoliday,
      events: dayEvents,
    });

    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
  }

  return months;
};
